import React, { useState, useMemo } from 'react';
import { Grid, TextField, IconButton, Button, Tooltip, Typography, Box } from '@material-ui/core';
import FormSection from '../../../components/atom/FormSection';
import { PlusIcon, DeleteIcon, CheckIcon, TimesIcon, EditIcon } from '../../../icons';
import moment from 'moment';

export default function AgentNotesSection({ values, setFieldValue, bookingCanModify, user }) {
    const [isAdding, setIsAdding] = useState(false);
    const [newNoteText, setNewNoteText] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');

    // Original notes array (for updates)
    const originalNotes = useMemo(() => values.agentnote?.agentnotes || [], [
        values.agentnote?.agentnotes
    ]);

    // Display oldest to newest for context
    const displayNotes = useMemo(() => originalNotes, [originalNotes]);

    const updateNotes = newNotes => {
        setFieldValue('agentnote', {
            ...values.agentnote,
            agentnotes: newNotes
        });
    };

    // Check if note is already saved (has created_at from server)
    const isNewNote = note => !note.created_at;

    const handleAddNote = () => {
        if (!newNoteText.trim()) {
            return;
        }

        const newNote = {
            id: `temp-${Date.now()}`,
            note: newNoteText.trim(),
            author: user?.id || 0,
            authorName: user ? `${user.firstname} ${user.lastname}` : 'Unknown'
            // No created_at - marks it as new/unsaved
        };

        updateNotes([...originalNotes, newNote]);
        setNewNoteText('');
        setIsAdding(false);
    };

    const handleEditStart = note => {
        setEditingId(note.id);
        setEditText(note.note || '');
    };

    const handleEditSave = () => {
        if (!editText.trim()) {
            return;
        }

        const updatedNotes = originalNotes.map(n =>
            n.id === editingId ? { ...n, note: editText.trim() } : n
        );

        updateNotes(updatedNotes);
        setEditingId(null);
        setEditText('');
    };

    const handleEditCancel = () => {
        setEditingId(null);
        setEditText('');
    };

    const handleDelete = noteId => {
        updateNotes(originalNotes.filter(n => n.id !== noteId));
    };

    const handleCancelAdd = () => {
        setIsAdding(false);
        setNewNoteText('');
    };

    return (
        <FormSection title="Agent Notes">
            <Grid container direction="column" spacing={2}>
                {displayNotes.length === 0 && !isAdding && (
                    <Grid item>
                        <Typography variant="body2" color="textSecondary">
                            No agent notes yet.
                        </Typography>
                    </Grid>
                )}

                {displayNotes.map(note => {
                    const noteText = note.note || '';
                    const isNew = isNewNote(note);
                    const isEditing = editingId === note.id;

                    return (
                        <Grid item key={note.id}>
                            <Box
                                p={1.5}
                                borderRadius={4}
                                style={{
                                    backgroundColor: isNew ? 'rgba(0, 128, 0, 0.05)' : '#f5f5f5'
                                }}
                            >
                                {/* Header: Author + DateTime */}
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={0.5}
                                >
                                    <Typography
                                        variant="caption"
                                        style={{ fontWeight: 600, color: '#555' }}
                                    >
                                        {note.authorName || 'Unknown Author'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        style={{ fontWeight: 600, color: '#555' }}
                                    >
                                        <span style={{ fontWeight: 400 }}>
                                            {note.created_at
                                                ? moment(note.created_at).format(
                                                      'MMM D, YYYY [at] h:mm A'
                                                  )
                                                : 'Not saved yet'}
                                        </span>
                                    </Typography>
                                </Box>

                                {/* Note content or edit field */}
                                {isEditing ? (
                                    <Box>
                                        <TextField
                                            value={editText}
                                            onChange={e => setEditText(e.target.value)}
                                            fullWidth
                                            multiline
                                            autoFocus
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Box mt={1}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                onClick={handleEditSave}
                                                disabled={!editText.trim()}
                                                style={{ marginRight: 8 }}
                                            >
                                                <CheckIcon style={{ marginRight: 4 }} /> Save
                                            </Button>
                                            <Button size="small" onClick={handleEditCancel}>
                                                <TimesIcon style={{ marginRight: 4 }} /> Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{ whiteSpace: 'pre-wrap', flex: 1 }}
                                        >
                                            {noteText}
                                        </Typography>

                                        {bookingCanModify && (
                                            <Box ml={1} style={{ whiteSpace: 'nowrap' }}>
                                                {/* Only new notes can be edited */}
                                                {isNew && (
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditStart(note)}
                                                            disabled={
                                                                isAdding || editingId !== null
                                                            }
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {/* All notes can be deleted */}
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(note.id)}
                                                        disabled={isAdding || editingId !== null}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    );
                })}

                {/* Add new note form */}
                {isAdding ? (
                    <Grid item>
                        <Box p={1.5} borderRadius={4} style={{ backgroundColor: '#e3f2fd' }}>
                            <Typography
                                variant="caption"
                                style={{ fontWeight: 600, color: '#555' }}
                            >
                                {user ? `${user.firstname} ${user.lastname}` : 'You'}
                            </Typography>
                            <TextField
                                value={newNoteText}
                                onChange={e => setNewNoteText(e.target.value)}
                                fullWidth
                                multiline
                                autoFocus
                                placeholder="Enter note..."
                                size="small"
                                variant="outlined"
                                style={{ marginTop: 8 }}
                            />
                            <Box mt={1}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddNote}
                                    disabled={!newNoteText.trim()}
                                    style={{ marginRight: 8 }}
                                >
                                    Add Note
                                </Button>
                                <Button size="small" onClick={handleCancelAdd}>
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                ) : (
                    bookingCanModify && (
                        <Grid item>
                            <Button
                                size="small"
                                startIcon={<PlusIcon />}
                                onClick={() => setIsAdding(true)}
                                disabled={editingId !== null}
                            >
                                Add Note
                            </Button>
                        </Grid>
                    )
                )}
            </Grid>
        </FormSection>
    );
}
